import { FC, useState } from "react";
import { LineStylePopup, ColorPickerPopup } from "shared/ui";
import type { MACDSettings, IndicatorLineStyle } from "../../model";
import styles from "./ADXStylesTab.module.css";

type LineKey = "macd" | "signal";
type PopupKey = "upColor" | "downColor" | LineKey;

const LINE_ROWS: { key: LineKey; label: string }[] = [
  { key: "macd", label: "macd" },
  { key: "signal", label: "signal" },
];

interface Props {
  settings: MACDSettings;
  onChange: (s: MACDSettings) => void;
}

export const MACDStylesTab: FC<Props> = ({ settings, onChange }) => {
  const [openPopup, setOpenPopup] = useState<PopupKey | null>(null);
  const toggle = (key: PopupKey) => setOpenPopup((prev) => (prev === key ? null : key));
  const updateLine = (key: LineKey, patch: Partial<IndicatorLineStyle>) =>
    onChange({ ...settings, [key]: { ...settings[key], ...patch } });

  return (
    <div className={styles.tab}>
      <div className={styles.row}>
        <div className={styles.left}>
          <label className={styles.checkboxWrap}>
            <input
              type="checkbox"
              className={styles.checkboxInput}
              checked={settings.histogramVisible}
              onChange={(e) => onChange({ ...settings, histogramVisible: e.target.checked })}
            />
            <span className={styles.checkmark} />
          </label>
          <span className={styles.label}>histogram</span>
        </div>
      </div>
      {(["upColor", "downColor"] as const).map((key) => (
        <div key={key} className={styles.row}>
          <div className={styles.left}>
            <span style={{ width: 16, height: 16, flexShrink: 0, display: "block" }} />
            <span className={styles.label}>{key === "upColor" ? "up" : "down"}</span>
          </div>
          <div className={styles.btnWrap}>
            <button className={styles.colorBtn} onMouseDown={(e) => e.stopPropagation()} onClick={() => toggle(key)}>
              <span className={styles.colorBtnSwatch} style={{ background: settings[key] }} />
            </button>
            {openPopup === key && (
              <ColorPickerPopup color={settings[key]} onChange={(c) => onChange({ ...settings, [key]: c })} onClose={() => setOpenPopup(null)} />
            )}
          </div>
        </div>
      ))}
      {LINE_ROWS.map(({ key, label }) => (
        <div key={key} className={styles.row}>
          <div className={styles.left}>
            <label className={styles.checkboxWrap}>
              <input type="checkbox" className={styles.checkboxInput} checked={settings[key].visible}
                onChange={(e) => updateLine(key, { visible: e.target.checked })} />
              <span className={styles.checkmark} />
            </label>
            <span className={styles.label}>{label}</span>
          </div>
          <div className={styles.btnWrap}>
            <button className={styles.colorBtn} onMouseDown={(e) => e.stopPropagation()} onClick={() => toggle(key)}>
              <span className={styles.colorBtnSwatch} style={{ background: settings[key].color }} />
              <span className={styles.colorBtnWidth}>{settings[key].width}px</span>
            </button>
            {openPopup === key && (
              <LineStylePopup color={settings[key].color} width={settings[key].width}
                onColorChange={(c) => updateLine(key, { color: c })} onWidthChange={(w) => updateLine(key, { width: w })}
                onClose={() => setOpenPopup(null)} />
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
